import React, { useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Lock, LockOpen } from '@mui/icons-material';
import { clearUserMfa } from '../../services/userDetailService';
import { formatDate } from '../../services/adminService';

/**
 * `mfa_enrolled_at` is a nullable RFC3339 string, matching created_at and
 * last_login on the same response — **not** Unix seconds like GET /mfa's
 * enrolled_at. So it goes through formatDate directly, and must never be routed
 * through mfaService.formatEnrolledAt, which multiplies by 1000 and would land
 * this in the far future.
 *
 * Branched rather than interpolated: formatDate answers 'Never' for a null,
 * which would read as "Enrolled since Never".
 */
const enrollmentSummary = (enrolledAt) =>
  enrolledAt
    ? `Enrolled since ${formatDate(enrolledAt)}.`
    : 'No enrollment on record.';

/**
 * Staff break-glass: clear a user's second factor when they can no longer
 * produce one.
 *
 * This is the mirror of the account page's Security tab and shares nothing with
 * it. That one acts on the bearer token's own user and demands a password and a
 * current code; this one names a user in the path and demands **neither** — the
 * operator's can_manage_user_mfa permission is the entire check, and the
 * server's audit log is the only other record that it happened.
 *
 * Hence the typed confirmation. There is no second proof to ask for, so the
 * friction has to come from the UI: the operator types the username, which
 * makes it hard to weaken the wrong account by clicking through a dialog on the
 * wrong browser tab. It is a slip guard, not a security control — anyone with
 * the permission can do this, and the point is that they meant to.
 *
 * ## Why "no enrollment on record" does not disable the action
 *
 * `mfa_enrolled_at` is the database stamp, and the database is not what decides
 * whether a user gets challenged at sign-in — the identity provider is.
 * `auth_service.go:73` asks the provider, and nothing in internal/authorization
 * reads MFAEnrolledAt at all.
 *
 * Those two can disagree. CompleteEnrollment finalizes with the provider and
 * *then* stamps the row (`mfa_service.go:118-138`), so a failure between the two
 * writes leaves the provider holding a factor that the stamp knows nothing
 * about. A user in that state is challenged at sign-in, cannot produce a code if
 * the device is gone, and cannot re-enroll either — StartEnrollment consults the
 * provider as well as the cache and answers ALREADY_ENROLLED.
 *
 * That is precisely the person break-glass exists for, and the response would
 * show them as having no enrollment. Disabling Clear on a null would therefore
 * withdraw the recovery path in the one case that most needs it. So the state is
 * reported honestly, the copy changes, and the action stays available —
 * ClearUserMFA clears the provider first and the stamp second
 * (`admin_service.go:295-305`), which is the order that repairs the mismatch.
 *
 * A non-null stamp always carries a real time, so there is no
 * enrolled-at-unknown-time case to render here.
 */
function SecurityTab({ detail, onCleared }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The id we cleared, not a boolean. /admin/user/:id keeps this component
  // mounted across a change of id, so a plain flag would leave the success
  // banner up while displayName re-derived from the new user — announcing that
  // somebody's MFA was cleared when it was not. Keying the state to the subject
  // makes the banner belong to one user by construction, with no effect to fire
  // on every re-fetch of the same one.
  const [clearedFor, setClearedFor] = useState(null);

  const close = useCallback(() => {
    setConfirming(false);
    setTyped('');
    setError(null);
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await clearUserMfa(detail.id);
      setClearedFor(detail.id);
      close();
      if (onCleared) onCleared();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const displayName =
    [detail.first_name, detail.last_name].filter(Boolean).join(' ') || detail.username;
  const enrolled = Boolean(detail.mfa_enrolled_at);

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: '1.125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        Security
      </Typography>

      {clearedFor === detail.id && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setClearedFor(null)}>
          {`Multi-factor authentication has been cleared for ${displayName}.`}
        </Alert>
      )}

      {/* A failure is only ever reported inside the dialog, which stays open
          when the call fails — see the Alert in DialogContent below. There is no
          out-of-dialog failure path: the clear can only be started from the
          dialog, and the only thing that closes it also clears the error. */}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 2 }}>
        <Box sx={{ color: enrolled ? 'success.main' : 'text.secondary', mt: 0.5 }}>
          {enrolled ? <Lock /> : <LockOpen />}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Multi-factor authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {enrollmentSummary(detail.mfa_enrolled_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {enrolled
              ? 'Clearing it lets them sign in with their password and enroll again. Use this only when they can no longer produce a code.'
              : 'If they are still asked for a code at sign-in, clear it anyway — in rare cases the record lags the authenticator, and clearing repairs both.'}
          </Typography>
        </Box>
        <Button variant="outlined" color="error" onClick={() => setConfirming(true)}>
          Clear
        </Button>
      </Box>

      <Dialog open={confirming} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
        <DialogTitle>Clear multi-factor authentication?</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            {`This removes every second factor on ${displayName}'s account, leaving it protected
              by password alone until they enroll again. It cannot be undone from here, and it
              is recorded against your account.`}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Type <strong>{detail.username}</strong> to confirm.
          </DialogContentText>
          <TextField
            id="clear-mfa-confirm"
            label="Username"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={busy}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={typed !== detail.username || busy}
            onClick={confirm}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SecurityTab;
